// Express cart lines backed by a session's real cart: the agent's View Cart
// link (/cart), or a brand link (/cart/:slug) once the shopper has a session.
//
// Lines start from the cart's rows, plus a starting line for any link product
// that isn't in the cart yet, and every edit is written back with the checkout
// function's POST /update-cart (supabase/functions/checkout/checkout-endpoints.md
// in nile-ai-mastra), one row per call.

import type { CartItem } from "@/types";
import { restoreLines, type CheckoutLine, type CheckoutLink } from "./checkout";
import { findVariant, sellableVariants, variantValues, type CheckoutProduct } from "./variants";

/**
 * The variant SKU a cart row was saved with, or null for a row without one.
 * get_cart returns the variant's title in `selected_variant` and its SKU in
 * `sku`; for a row without a variant, `sku` is the product's own SKU.
 */
export function rowVariant(item: Pick<CartItem, "selected_variant" | "sku">): string | null {
  return item.selected_variant ? item.sku : null;
}

/** Products the page offers: the cart's, in cart order, then the link's. */
export function sessionCartProducts(
  products: CheckoutProduct[],
  cartItems: Pick<CartItem, "product_id">[],
  linkProductIds: string[],
): CheckoutProduct[] {
  const byId = new Map(products.map((p) => [p.id, p]));
  const ids = [...new Set([...cartItems.map((i) => i.product_id), ...linkProductIds])];
  return ids.map((id) => byId.get(id)).filter((p): p is CheckoutProduct => Boolean(p));
}

/**
 * One line per cart row, plus a starting line for each link product not in the
 * cart. With an empty cart those start at 1 (0 when out of stock), the same as
 * a fresh link. Once the cart has items, only the cart's own quantities count:
 * the other link products start at 0, still on the page (greyed out) to add.
 */
export function linesFromCart(link: CheckoutLink, cartItems: CartItem[]): CheckoutLine[] {
  const saved = cartItems.flatMap((item): CheckoutLine[] => {
    const product = link.products.find((p) => p.id === item.product_id);
    if (!product) return [];
    const sku = rowVariant(item);
    const variant = sku ? sellableVariants(product).find((v) => v.sku === sku) : undefined;
    return [
      {
        clientKey: `cart:${item.id}`,
        cartItemId: item.id,
        productId: item.product_id,
        selection: variant ? variantValues(product, variant) : {},
        quantity: item.quantity,
      },
    ];
  });
  const lines = restoreLines(link, saved);
  if (saved.length === 0) return lines;
  const inCart = new Set(saved.map((line) => line.productId));
  return lines.map((line) => (inCart.has(line.productId) ? line : { ...line, quantity: 0 }));
}

/** Attaches server-generated cart row IDs without changing local UI keys. */
export function attachCartItemIds(
  link: CheckoutLink,
  lines: CheckoutLine[],
  cartItems: CartItem[],
): CheckoutLine[] {
  const itemsByRow = new Map(
    cartItems.map((item) => [rowKey(item.product_id, rowVariant(item)), item]),
  );
  return lines.map((line) => {
    const product = link.products.find((candidate) => candidate.id === line.productId);
    if (!product) return { ...line, cartItemId: undefined };
    const variant = hasVariants(product)
      ? findVariant(product, line.selection)?.sku ?? null
      : null;
    const item = itemsByRow.get(rowKey(line.productId, variant));
    return { ...line, cartItemId: item?.id };
  });
}

export interface CartRowChange {
  productId: string;
  /** Variant SKU to send as `selectedSku`, or null to send none. */
  variant: string | null;
  quantity: number;
}

function rowKey(productId: string, variant: string | null): string {
  return `${productId}|${variant ?? ""}`;
}

function hasVariants(product: CheckoutProduct): boolean {
  return sellableVariants(product).length > 0;
}

/**
 * The rows the cart should hold for these lines. Lines at 0 aren't in it, and
 * lines still waiting for options aren't saved: POST /update-cart can't create
 * a row without a variant for a product that has variants.
 */
export function desiredRows(link: CheckoutLink, lines: CheckoutLine[]): Map<string, CartRowChange> {
  const rows = new Map<string, CartRowChange>();
  for (const line of lines) {
    if (line.quantity <= 0) continue;
    const product = link.products.find((p) => p.id === line.productId);
    if (!product) continue;
    const variant = hasVariants(product) ? findVariant(product, line.selection)?.sku ?? null : null;
    if (hasVariants(product) && !variant) continue;
    const key = rowKey(line.productId, variant);
    rows.set(key, { productId: line.productId, variant, quantity: line.quantity });
  }
  return rows;
}

/**
 * The next single POST /update-cart call that brings the session's rows closer
 * to the page's lines, or null when they match. Call it again with the cart
 * that call returns: the endpoint changes one row at a time, and setting a
 * variant SKU that isn't in the cart turns the product's no-variant row into
 * that variant, so later steps depend on the cart after each call.
 */
export function nextCartChange(link: CheckoutLink, lines: CheckoutLine[], cartItems: CartItem[]): CartRowChange | null {
  const desired = desiredRows(link, lines);
  const current = new Map<string, CartRowChange>();
  for (const item of cartItems) {
    // Never touch rows whose product the page couldn't load: it has no line for
    // them, and treating that as "removed" would delete the shopper's items.
    if (!link.products.some((p) => p.id === item.product_id)) continue;
    const variant = rowVariant(item);
    current.set(rowKey(item.product_id, variant), { productId: item.product_id, variant, quantity: item.quantity });
  }
  const productOf = (id: string) => link.products.find((p) => p.id === id);

  // 1. Rows to create or re-quantify (this also gives a no-variant row its variant).
  for (const [key, row] of desired) {
    if (current.get(key)?.quantity !== row.quantity) return row;
  }

  // 2. Variant rows no line wants any more.
  for (const [key, row] of current) {
    if (row.variant !== null && !desired.has(key)) return { ...row, quantity: 0 };
  }

  // 3. No-variant rows no line wants any more.
  for (const [key, row] of current) {
    if (row.variant !== null || desired.has(key)) continue;
    const product = productOf(row.productId);
    if (product && hasVariants(product)) {
      // Kept while a line for it is still waiting for options.
      const waiting = lines.some(
        (l) => l.productId === row.productId && l.quantity > 0 && !findVariant(product, l.selection),
      );
      if (waiting) continue;
      // Without a SKU the call is ambiguous when the product has other rows;
      // a SKU that isn't in the cart targets the no-variant row instead.
      const otherRows = cartItems.filter((i) => i.product_id === row.productId).length > 1;
      if (otherRows) {
        const inCart = new Set(cartItems.filter((i) => i.product_id === row.productId).map(rowVariant));
        const free = sellableVariants(product).find((v) => !inCart.has(v.sku));
        if (free) return { productId: row.productId, variant: free.sku, quantity: 0 };
      }
    }
    return { ...row, quantity: 0 };
  }

  return null;
}

/**
 * Removes lines that went to 0 for products that aren't pinned: cart items
 * that didn't come from the link disappear once removed, while the link's own
 * products keep their (greyed-out) line so they can be added back.
 */
export function dropRemovedLines(link: CheckoutLink, lines: CheckoutLine[]): CheckoutLine[] {
  if (!link.pinnedProductIds) return lines;
  const pinned = new Set(link.pinnedProductIds);
  return lines.filter((line) => line.quantity > 0 || pinned.has(line.productId));
}

/**
 * The product behind a product's cart rows, built from the rows themselves:
 * get_cart returns each row's product variants (`variants`), so the shopper can
 * choose options for items already in the cart.
 *
 * Rows don't carry the product's stock count. A product without variants that
 * is in stock can go up to the usual per-line maximum (update-cart checks the
 * real stock and reports "only N in stock"); one that's out of stock stays at 0.
 * Rows from an older get_cart without `variants` fall back to their own chosen
 * variant, capped at the quantity already in the cart.
 */
export function productFromCartRows(rows: CartItem[]): CheckoutProduct | null {
  const [first] = rows;
  if (!first) return null;
  const base = {
    id: first.product_id,
    sku: first.base_product_sku ?? null,
    fulfillment_brand_id: first.fulfillment_brand_id ?? null,
    fulfillment_brand_name: first.fulfillment_brand_name ?? null,
    title: first.title,
    price_cents: first.unit_price_cents,
    currency: "ZAR",
    image_url: first.image_url ?? first.variant_image ?? null,
    shopify_variant_id: null,
    options: null,
  };

  const listed = rows.find((row) => Array.isArray(row.variants))?.variants;
  if (listed) {
    const variants = listed as CheckoutProduct["variants"];
    if (variants.length > 0) return { ...base, inventory_count: null, track_inventory: true, variants };
    // No variants: the row only says whether it's in stock.
    return first.in_stock
      ? { ...base, inventory_count: null, track_inventory: false, variants: [] }
      : { ...base, inventory_count: 0, track_inventory: true, variants: [] };
  }

  const stockOf = (row: CartItem) => (row.in_stock ? row.quantity : 0);
  const variantRows = rows.filter((row) => rowVariant(row) !== null);
  return {
    ...base,
    image_url: first.variant_image ?? first.image_url,
    inventory_count: variantRows.length === 0 ? stockOf(first) : null,
    track_inventory: true,
    variants: variantRows.map((row) => ({
      sku: row.sku,
      title: row.selected_variant ?? row.sku,
      price: row.unit_price_cents,
      stock: stockOf(row),
      image: row.variant_image ?? null,
    })),
  };
}
